/**
 * Chat state types for the AI chat right pane.
 *
 * This module defines the frontend state model only - no WebSocket logic here.
 * See chatReducer.ts for the pure reducer that transforms these types.
 */

import type {
  AiStructuredCardActionState,
  AiStructuredPayload,
  ChatMode,
  ChatTriggerSource,
  RunOrigin,
  ExecutionStatus,
  ExplicitContextItem,
  GeminiModel,
  RagReference,
  RunLifecycleState,
  RunSegment,
  SelectedContext,
  UserPromptChoice,
  UserPromptFileSpec,
  UserPromptKind,
  UserPromptLink,
  UserPromptQuestion,
} from '@protoss/shared';
import type { RunChildEntry } from '../../../lib/executionStatusHelpers';

// ─────────────────────────────────────────────────────────────────────────────
// Sub-message events inside one assistant run
// ─────────────────────────────────────────────────────────────────────────────

export type { RunChildEntry, StatusEntry, ToolEntry } from '../../../lib/executionStatusHelpers';

// ─────────────────────────────────────────────────────────────────────────────
// Ordered conversational segments inside one assistant run
//
// [Conversational copilot] A run renders as an interleaved transcript -
// text → tool chip → text → question → answer → text - instead of one text
// blob. `RunSegment`/`FormAnswerSummary` now live in @protoss/shared so the live
// client reducer AND the server-side transcript builder share one definition
// (the server persists the same projection so history restores runs as-is).
// Re-exported here for the existing local importers.
// ─────────────────────────────────────────────────────────────────────────────

export type { RunSegment, FormAnswerSummary } from '@protoss/shared';

// ─────────────────────────────────────────────────────────────────────────────
// One turn in the conversation
// ─────────────────────────────────────────────────────────────────────────────

export interface UserTurn {
  kind: 'user';
  id: string;
  prompt: string;
  context: SelectedContext;
  ts: string;
  /**
   * Who or what sent this user message.
   * 'user'     - typed/injected from the web UI
   * 'external' - pushed by an external agent or integration
   */
  initiator?: 'user' | 'external';
  /**
   * What initiated the run (shared ChatTriggerSource vocabulary). Button-driven
   * runs (enrich / analyze transcript / generate) render a pill under the bubble;
   * 'user_chat' renders nothing. Set optimistically at send time from the agentKey
   * and rehydrated from chat_message.trigger_source on reload.
   */
  triggerSource?: ChatTriggerSource;
  /**
   * Precise origin of a button-initiated run (page + exact button label), e.g.
   * { page: 'Deal', label: 'Regenerate' }. When present the pill renders this
   * verbatim; otherwise it falls back to the coarse triggerSource label. Set
   * optimistically at send time and rehydrated from chat_message.trigger_origin.
   */
  origin?: RunOrigin;
}

/**
 * AssistantRun represents one assistant turn, which may contain:
 * - streamed text (accumulated in `text`)
 * - status/tool child events
 * - RAG references
 * - a lifecycle state
 * - normalized execution status for progress tracking
 */
export interface AssistantRun {
  kind: 'assistant';
  runId: string;
  messageId: string | null;
  lifecycleState: RunLifecycleState;
  model: GeminiModel;
  mode: ChatMode;
  /** Accumulated text from message_delta events */
  text: string;
  /** Whether text is still arriving */
  streaming: boolean;
  /** @deprecated: Use executionStatus.steps instead. Kept for backward compatibility. */
  childEntries: RunChildEntry[];
  ragReferences: RagReference[];
  /** Normalized execution status model (optional for backward compatibility) */
  executionStatus?: ExecutionStatus;
  /** If awaiting confirmation, the confirmation prompt */
  confirmationPrompt?: string;
  confirmationDetails?: string;
  /**
   * [Phase 4D] Open human-in-the-loop prompt (confirm/choice/text) when the run paused
   * mid-execution to ask the user. Rendered while lifecycleState === 'awaiting_confirmation';
   * the user answers via respondPrompt(promptId, response).
   */
  pendingPrompt?: {
    promptId: string;
    kind: UserPromptKind;
    prompt: string;
    details?: string;
    choices?: UserPromptChoice[];
    /** Option id marked "(recommended)" and floated to the top of the choice list. */
    recommendedChoiceId?: string;
    /** When true, free-text answers are accepted alongside choices/confirm. */
    allowFreeText?: boolean;
    /** When true, suppress the default "Other" free-text affordance for this choice set. */
    closedChoices?: boolean;
    /** When true, render a checkbox multi-select with a Confirm action (answers with choiceIds). */
    multiSelect?: boolean;
    /** Multi-select: minimum options to check before Confirm enables. */
    minSelections?: number;
    /** Multi-select: maximum options accepted. */
    maxSelections?: number;
    suggestions?: string[];
    file?: UserPromptFileSpec;
    link?: UserPromptLink;
    questions?: UserPromptQuestion[];
    /** [HITL navigation] When true, Back/Change-answer affordances are offered. */
    canGoBack?: boolean;
  };
  /**
   * [Conversational copilot] Ordered transcript of this run (text / tool / prompt
   * segments). Absent on legacy and history runs - render falls back to `text`.
   */
  segments?: RunSegment[];
  /** Error code and message if lifecycleState === 'error' */
  errorCode?: string;
  errorMessage?: string;
  /** False for business-rule errors that should not be retried. Defaults to true. */
  retryable?: boolean;
  startedAt: string;
  endedAt?: string;
  /**
   * Structured payload from the skill - present when the assistant turn carries
   * a machine-readable response card (e.g. company enrichment result).
   * Received via the structured_payload WS event and persisted in content_json.
   */
  structuredPayload?: AiStructuredPayload;
  /**
   * The 0-based message index within the session - needed to call the
   * PATCH action-result endpoint. Set from history; null for live runs
   * until run_completed sets the messageId.
   */
  messageIndex?: number;
  /**
   * Persisted user action on the card - set after the user clicks Fill or Cancel.
   * Initialised from DB on history load; updated optimistically on click.
   */
  actionResult?: 'accepted' | 'dismissed';
  /**
   * Which field keys the user chose to fill - only meaningful when actionResult === 'accepted'.
   * Persisted as JSON inside the action_result DB column; rehydrated on history load.
   */
  acceptedFieldKeys?: string[];
  /**
   * Per-card action states for multi-card structured payloads (payload.cards),
   * keyed by card key. Persisted as {"cards":{...}} in the action_result column.
   */
  cardActionResults?: Record<string, AiStructuredCardActionState>;
  /** Token usage reported by run_completed - null until run finishes */
  tokenUsage?: {
    promptTokens: number;
    completionTokens: number;
    totalTokens: number;
    /** Peak single-call input tokens (context-window occupancy); feeds the usage meter. */
    contextTokens?: number;
  };
  /**
   * Who or what initiated this run.
   * 'user'     - typed/injected from the web UI
   * 'external' - injected by an external agent (e.g. enrichment, pipeline)
   */
  initiator?: 'user' | 'external';
  /**
   * Persisted context snapshot from the backend (used to reconstruct UI state
   * from historical runs, e.g. for transcriptDocUuid).
   */
  contextSnapshot?: any;
}

export type ConversationTurn = UserTurn | AssistantRun;

// ─────────────────────────────────────────────────────────────────────────────
// Session list item (from REST API)
// ─────────────────────────────────────────────────────────────────────────────

export interface ChatSessionSummary {
  uuid: string;
  title: string | null;
  mode: string;
  status: string;
  lastMessageAt: string | null;
  createdAt: string;
  /**
   * [Phase 3] When set, this session has an AI run still in flight (server-side). On a
   * cold load after a hard refresh the client re-attaches to it via subscribe_run so the
   * step drawer + streaming result resume instead of showing a blank panel until the run
   * finishes. Null when the session has no active run.
   */
  activeRunId?: string | null;
  /** [Phase 3] The active run's original user prompt, used to seed the user bubble on resume. */
  activeRunPrompt?: string | null;
}

export type SessionsStatus = 'idle' | 'loading' | 'loaded' | 'error';

// ─────────────────────────────────────────────────────────────────────────────
// Top-level chat state
// ─────────────────────────────────────────────────────────────────────────────

export type ChatConnectionStatus = 'idle' | 'connecting' | 'connected' | 'disconnected' | 'error';

export type HistoryStatus = 'idle' | 'loading' | 'loaded' | 'error';

// ─────────────────────────────────────────────────────────────────────────────
// Per-session state slice
//
// Holds the per-session fields that can be preserved when switching away.
// The active session's slice is projected to the top-level ChatState fields;
// background sessions' slices are kept in ChatState.sessionPool.
// ─────────────────────────────────────────────────────────────────────────────

export interface SessionSlice {
  connectionStatus: ChatConnectionStatus;
  /** Number of reconnect attempts made for this session. Reset to 0 on successful connection. */
  reconnectAttempt: number;
  conversationId: string | null;
  turns: ConversationTurn[];
  historyStatus: HistoryStatus;
  activeRunId: string | null;
  hasMoreHistory: boolean;
  oldestMessageIndex: number | null;
  lastSeq: number;
  /**
   * [Phase 3] Highest per-RUN sequence (event.runSeq) applied for lastRunSeqRunId.
   * Used to de-dup replayed events after a reconnect (subscribe_run replays
   * runSeq > lastRunSeq). Distinct from lastSeq (the per-connection sequence).
   */
  lastRunSeq: number;
  /** [Phase 3] The runId that lastRunSeq tracks; resets dedup when a new run starts. */
  lastRunSeqRunId: string | null;
  /** True when this background session received a completed AI response the user hasn't viewed yet. */
  hasUnread: boolean;
}

export const initialSessionSlice: SessionSlice = {
  connectionStatus: 'idle',
  reconnectAttempt: 0,
  conversationId: null,
  turns: [],
  historyStatus: 'idle',
  activeRunId: null,
  hasMoreHistory: false,
  oldestMessageIndex: null,
  lastSeq: 0,
  lastRunSeq: 0,
  lastRunSeqRunId: null,
  hasUnread: false,
};

export interface ChatState {
  /**
   * The composer's selected ChatMode (Ask/Agent/Auto) - shared by every composer
   * surface (landing + rails). Seeded from the active session's stored mode;
   * sent with every run (server persists it as the session's last-used mode).
   */
  composerMode: ChatMode;
  connectionStatus: ChatConnectionStatus;
  /** Number of reconnect attempts for the active session. Reset to 0 on successful connection. */
  reconnectAttempt: number;
  conversationId: string | null;
  turns: ConversationTurn[];
  /** Status of loading prior chat history from the server */
  historyStatus: HistoryStatus;
  /** The runId of the currently active run, or null */
  activeRunId: string | null;
  /** Selected context for the next run */
  selectedContext: SelectedContext;
  /**
   * Explicit context items accumulated from project menu interactions.
   *
   * These are user-intentionally selected items (repo files, directories,
   * Monaco line ranges, and - in the future - board items, pipeline runs,
   * docs, etc.) that appear as removable chips above the chat input.
   *
   * Items are additive: clicking a new file/dir adds to this list rather than
   * replacing. Each item has a stable `id` for per-chip removal.
   *
   * At send-time, AiChatPane merges these into the SelectedContext sent with
   * the run. They do NOT replace `selectedContext`; they extend it.
   */
  explicitContextItems: ExplicitContextItem[];
  /**
   * The single pending / "draft" explicit context item.
   *
   * When the user clicks a repo node or adds a Monaco line-range, the item
   * lands here first as a visually-distinct draft chip (italic, lighter colour).
   * Clicking the draft chip promotes it to `explicitContextItems` (confirmed).
   * At send-time the draft is included in the context alongside confirmed items,
   * so the user can send without explicitly confirming.
   *
   * Only one draft exists at a time - a new click replaces any previous draft.
   */
  draftExplicitItem: ExplicitContextItem | null;
  /** Per-conversation sequence tracking */
  lastSeq: number;
  /** [Phase 3] Highest per-run sequence applied (for the run lastRunSeqRunId tracks). */
  lastRunSeq: number;
  /** [Phase 3] The runId that lastRunSeq tracks. */
  lastRunSeqRunId: string | null;
  /** Summary list of available sessions for the selector */
  sessions: ChatSessionSummary[];
  /** Status of the sessions list fetch */
  sessionsStatus: SessionsStatus;
  /** UUID of the currently displayed session, or null (showing all history) */
  activeSessionUuid: string | null;
  /** Whether there are older messages available to paginate back through */
  hasMoreHistory: boolean;
  /**
   * The smallest messageIndex of the currently loaded messages.
   * Used as the `before` cursor for the next "load older" request.
   */
  oldestMessageIndex: number | null;
  /**
   * Live per-session state pool for background sessions.
   *
   * Keyed by session UUID. When the user switches from session A to session B,
   * session A's current slice (connectionStatus, turns, activeRunId, …) is saved
   * here. While session A is in the background, its dedicated WS connection
   * continues delivering events - those events update this pool entry so state
   * stays live. When the user switches back to A, the slice is restored to the
   * top-level fields and removed from the pool.
   *
   * Unlike the old `sessionSnapshots` model, `activeRunId` is preserved as its
   * actual value (not forced to null), because each session now has its own WS
   * connection and background events are tracked correctly.
   */
  sessionPool: Record<string, SessionSlice>;
}

export const initialChatState: ChatState = {
  composerMode: 'ask',
  connectionStatus: 'idle',
  reconnectAttempt: 0,
  conversationId: null,
  turns: [],
  historyStatus: 'idle',
  activeRunId: null,
  selectedContext: {},
  explicitContextItems: [],
  draftExplicitItem: null,
  lastSeq: 0,
  lastRunSeq: 0,
  lastRunSeqRunId: null,
  sessions: [],
  sessionsStatus: 'idle',
  activeSessionUuid: null,
  hasMoreHistory: false,
  oldestMessageIndex: null,
  sessionPool: {},
};
